import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/formatters';
import { Users, Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

function CustomerRow({ customer, onEdit, onDelete }) {
  return (
    <tr>
      <td>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-primary-900 font-semibold">
            {customer.name?.charAt(0) || 'C'}
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{customer.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Kode: {customer.code}</p>
          </div>
        </div>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{customer.contact_person || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400">{customer.phone || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400">{customer.email || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatCurrency(customer.credit_limit)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400">{customer.payment_terms || '-'}</span>
      </td>
      <td>
        <span className={`badge ${customer.is_active ? 'badge-success' : 'badge-danger'}`}>
          {customer.is_active ? 'Aktif' : 'Nonaktif'}
        </span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onEdit(customer)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(customer)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CustomersPage() {
  const { hasPermission } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', contact_person: '', phone: '', email: '', address: '', credit_limit: '', payment_terms: '', is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, [pagination.page, filters]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/customers', { params });
      if (res.data.success) {
        setCustomers(res.data.data?.customers || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/customers', form);
      if (res.data.success) {
        toast.success('Pelanggan berhasil dibuat');
        setShowCreateModal(false);
        setForm({ code: '', name: '', contact_person: '', phone: '', email: '', address: '', credit_limit: '', payment_terms: '', is_active: true });
        fetchCustomers();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/customers/${selectedCustomer.id}`, form);
      if (res.data.success) {
        toast.success('Pelanggan berhasil diperbarui');
        setShowEditModal(false);
        setSelectedCustomer(null);
        fetchCustomers();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!confirm(`Yakin hapus pelanggan ${customer.name}?`)) return;
    try {
      const res = await api.delete(`/customers/${customer.id}`);
      if (res.data.success) {
        toast.success('Pelanggan berhasil dihapus');
        fetchCustomers();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setForm({
      code: customer.code || '',
      name: customer.name || '',
      contact_person: customer.contact_person || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      credit_limit: customer.credit_limit || '',
      payment_terms: customer.payment_terms || '',
      is_active: customer.is_active,
    });
    setShowEditModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Manajemen Pelanggan</h1>
          <p className="page-subtitle">Kelola data pelanggan dan informasi kredit</p>
        </div>
        {hasPermission('create_customers') && (
          <button onClick={() => { setForm({ code: '', name: '', contact_person: '', phone: '', email: '', address: '', credit_limit: '', payment_terms: '', is_active: true }); setShowCreateModal(true); }} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" /> Tambah Pelanggan
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari kode, nama, kontak..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchCustomers()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={fetchCustomers} className="btn-primary btn-sm">Cari</button>
              <button onClick={() => setFilters({ search: '', status: '' })} className="btn-secondary btn-sm">Reset</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-40" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : customers.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada pelanggan yang terdaftar.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pelanggan</th>
                    <th>Kontak</th>
                    <th>Telepon</th>
                    <th>Email</th>
                    <th>Batas Kredit</th>
                    <th>Syarat Bayar</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <CustomerRow
                      key={c.id}
                      customer={c}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Tambah Pelanggan Baru</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Kode <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required placeholder="Contoh: CUST-001" />
                </div>
                <div>
                  <label className="label">Nama <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nama pelanggan" />
                </div>
                <div>
                  <label className="label">Kontak Person</label>
                  <input type="text" className="input" value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} placeholder="Nama kontak PIC" />
                </div>
                <div>
                  <label className="label">Telepon <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required placeholder="Nomor telepon" />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input type="email" className="input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="Email pelanggan" />
                </div>
                <div>
                  <label className="label">Batas Kredit</label>
                  <input type="number" className="input" value={form.credit_limit} onChange={(e) => setForm({...form, credit_limit: e.target.value})} placeholder="Batas kredit dalam Rupiah" />
                </div>
                <div>
                  <label className="label">Syarat Bayar</label>
                  <input type="text" className="input" value={form.payment_terms} onChange={(e) => setForm({...form, payment_terms: e.target.value})} placeholder="Contoh: 30 hari, Cash, etc" />
                </div>
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Alamat</label>
                  <textarea className="input min-h-[80px]" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Alamat lengkap pelanggan" rows={3} />
                </div>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCustomer && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Edit Pelanggan</h3>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Kode <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} required />
                </div>
                <div>
                  <label className="label">Nama <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                </div>
                <div>
                  <label className="label">Kontak Person</label>
                  <input type="text" className="input" value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} />
                </div>
                <div>
                  <label className="label">Telepon <span className="text-danger-600">*</span></label>
                  <input type="text" className="input" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input type="email" className="input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                </div>
                <div>
                  <label className="label">Batas Kredit</label>
                  <input type="number" className="input" value={form.credit_limit} onChange={(e) => setForm({...form, credit_limit: e.target.value})} />
                </div>
                <div>
                  <label className="label">Syarat Bayar</label>
                  <input type="text" className="input" value={form.payment_terms} onChange={(e) => setForm({...form, payment_terms: e.target.value})} />
                </div>
                <div>
                  <label className="label">Status</label>
                  <select className="input" value={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.value === 'true'})}>
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Alamat</label>
                  <textarea className="input min-h-[80px]" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} rows={3} />
                </div>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedCustomer(null); }} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Perbarui'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}